import React, { useEffect, useState } from 'react';
import DashboardShell from '@/components/DashboardShell';
import api from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import AdminTable from '@/components/AdminTable';

const EMPTY_FORM = { name: '', description: '', active: true };

export default function Categories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formDialog, setFormDialog] = useState(null); // null | 'create' | category object
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchCategories = () => {
    setLoading(true);
    api.get('/admin/categories')
      .then(r => setCategories(r.data))
      .catch(() => toast.error('Failed to load categories'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchCategories(); }, []);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setFormDialog('create');
  };

  const openEdit = (cat) => {
    setForm({ name: cat.name, description: cat.description || '', active: cat.active });
    setFormDialog(cat);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Category name is required'); return; }
    setSubmitting(true);
    try {
      if (formDialog === 'create') {
        await api.post('/admin/categories', form);
        toast.success('Category created');
      } else {
        await api.put(`/admin/categories/${formDialog.id}`, form);
        toast.success('Category updated');
      }
      setFormDialog(null);
      fetchCategories();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save category');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/categories/${deleteId}`);
      toast.success('Category deleted');
      setDeleteId(null);
      fetchCategories();
    } catch {
      toast.error('Failed to delete category');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <DashboardShell title="Categories">
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{categories.length} categor{categories.length !== 1 ? 'ies' : 'y'}</p>
          <Button
            onClick={openCreate}
            style={{ backgroundColor: 'var(--text)', color: 'var(--bg)', borderRadius: '10px' }}
            data-testid="admin-create-category-button"
          >
            <Plus size={15} className="mr-1.5" /> Add category
          </Button>
        </div>

        <AdminTable
          data={categories}
          loading={loading}
          dataTestId="admin-categories-table"
          emptyTitle="No categories yet"
          emptySubtitle="Click “Add category” to create your first one."
          columns={[
            { key: 'name', label: 'Name', render: (cat) => <p className="font-medium" style={{ color: 'var(--text)' }}>{cat.name}</p> },
            { key: 'description', label: 'Description', render: (cat) => <p className="text-xs line-clamp-1" style={{ color: 'var(--text-muted)' }}>{cat.description || '—'}</p> },
            {
              key: 'active', label: 'Active',
              render: (cat) => (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cat.active ? 'bg-[#D1FAE5] text-[#065F46]' : 'bg-[#F3F4F6] text-[#374151]'}`}>
                  {cat.active ? 'Active' : 'Inactive'}
                </span>
              ),
            },
            {
              key: 'actions', label: '', width: '100px',
              render: (cat) => (
                <div className="flex items-center gap-2 justify-end">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(cat)} data-testid="admin-edit-category-button">
                    <Pencil size={13} />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDeleteId(cat.id)} style={{ color: 'var(--danger)' }} data-testid="admin-delete-category-button">
                    <Trash2 size={13} />
                  </Button>
                </div>
              ),
            },
          ]}
        />
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={!!formDialog} onOpenChange={() => setFormDialog(null)}>
        <DialogContent style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border-clr)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-serif)', color: 'var(--text)' }}>
              {formDialog === 'create' ? 'Add category' : 'Edit category'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-muted)' }}>Name <span style={{ color: 'var(--gold)' }}>·</span></label>
              <Input
                value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Photography"
                style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }}
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-muted)' }}>Description</label>
              <Textarea
                value={form.description}
                onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Short description…"
                rows={2}
                style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }}
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={form.active}
                onCheckedChange={v => setForm(p => ({ ...p, active: v }))}
                id="cat-active"
              />
              <label htmlFor="cat-active" className="text-sm cursor-pointer" style={{ color: 'var(--text)' }}>Active</label>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setFormDialog(null)} disabled={submitting} style={{ borderRadius: '10px' }}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              style={{ backgroundColor: 'var(--text)', color: 'var(--bg)', borderRadius: '10px' }}
            >
              {submitting ? 'Saving…' : formDialog === 'create' ? 'Create' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border-clr)' }}>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: 'var(--font-serif)', color: 'var(--text)' }}>Delete category?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: 'var(--text-muted)' }}>
              This will permanently delete the category. Listings using it will become uncategorised.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel style={{ borderRadius: '10px' }}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              style={{ backgroundColor: 'var(--danger)', color: 'white', borderRadius: '10px' }}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardShell>
  );
}
